import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../../components/ui/Button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';

import { useImportMenuFromImage } from '../../../../queries/signageMenus';
import { useCustomToast } from '../../../Contexts/ToastContext';

/**
 * AI Menu OCR — "Import from photo" dialog. Upload (or snap, on mobile) a photo
 * or PDF of a paper menu → Vertex AI Gemini reads it → returns a structured
 * DRAFT { currency, language, sections[], warnings[] }. Nothing is saved here:
 * we hand the draft to the parent (Menu Builder / Menu Editor) which pre-fills
 * its editable form so the user reviews + corrects before the normal save.
 *
 * Deliberately simple: pick → "Reading your menu…" → form filled. The form
 * itself is the review surface; warnings (cut-off text, unreadable bits) are
 * toasted so the user knows what to double-check.
 */
const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_BYTES = 15 * 1024 * 1024; // mirrors backend max:15360 KB

export default function MenuImportDialog({ visible, onHide, onImported }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const importMutation = useImportMenuFromImage();

  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);

  const reset = () => {
    setFile(null);
    setError(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const close = () => { reset(); onHide(); };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!f) return;
    setError(null);
    if (f.size > MAX_BYTES) {
      setError(t('video_wall.menu_ocr.too_large'));
      return;
    }
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  };

  const doImport = async () => {
    if (!file) return;
    setError(null);
    try {
      const res = await importMutation.mutateAsync(file);
      const draft = res?.draft;
      const itemCount = (draft?.sections ?? []).reduce((n, s) => n + (s.items?.length ?? 0), 0);

      if (res?.status === 422 || !draft || itemCount === 0) {
        setError(res?.msg || t('video_wall.menu_ocr.no_items'));
        return;
      }

      const warnings = draft.warnings ?? [];
      if (warnings.length) {
        showToast('warn', t('video_wall.menu_ocr.title'),
          t('video_wall.menu_ocr.warnings_toast', { n: warnings.length }));
      }
      showToast('success', t('video_wall.menu_ocr.title'),
        t('video_wall.menu_ocr.imported_toast', { items: itemCount, sections: draft.sections.length }));

      onImported(draft);
      close();
    } catch {
      // mutation onError already toasts network/throttle/validation errors
      setError(t('video_wall.menu_ocr.failed'));
    }
  };

  const importing = importMutation.isLoading;

  return (
    <Dialog visible={visible} onHide={close} style={{ width: 'min(95vw, 560px)' }}
      header={(
        <span><i className="pi pi-sparkles me-2" style={{ color: 'var(--pp-primary)' }} />
          {t('video_wall.menu_ocr.title')}</span>
      )}>
      {/* hidden inputs */}
      <input ref={fileRef} type="file" accept={ACCEPT} onChange={onPick} hidden />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onPick} hidden />

      {importing ? (
        <div className="text-center py-5">
          <ProgressSpinner style={{ width: 56, height: 56 }} strokeWidth="4" />
          <h6 className="mt-3 mb-1" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.menu_ocr.reading')}</h6>
          <p className="small mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_ocr.reading_hint')}</p>
        </div>
      ) : !file ? (
        <div className="pt-2">
          <p className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_ocr.intro')}</p>
          <div
            role="button"
            onClick={() => fileRef.current?.click()}
            className="d-flex flex-column align-items-center justify-content-center gap-2 p-5 mb-2"
            style={{ border: '2px dashed var(--pp-border-strong)', borderRadius: 'var(--pp-radius-md)', cursor: 'pointer', background: 'var(--pp-bg-secondary)' }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); onPick({ target: { files: e.dataTransfer.files, value: '' } }); }}>
            <i className="pi pi-cloud-upload" style={{ fontSize: '2rem', color: 'var(--pp-primary)' }} />
            <span style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>{t('video_wall.menu_ocr.drop_here')}</span>
            <span className="small" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.menu_ocr.formats')}</span>
          </div>
          <div className="d-flex gap-2">
            <Button className="flex-fill" outlined icon="pi pi-folder-open"
              label={t('video_wall.menu_ocr.choose_file')} onClick={() => fileRef.current?.click()} />
            <Button className="flex-fill" outlined icon="pi pi-camera"
              label={t('video_wall.menu_ocr.take_photo')} onClick={() => cameraRef.current?.click()} />
          </div>
          {error && <Message severity="error" text={error} className="w-100 mt-3" />}
        </div>
      ) : (
        <div className="pt-2">
          <div className="d-flex align-items-center gap-3 p-3 mb-3"
            style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)', border: '1px solid var(--pp-border)' }}>
            {previewUrl ? (
              <img src={previewUrl} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }} />
            ) : (
              <i className="pi pi-file-pdf" style={{ fontSize: '2.5rem', color: 'var(--pp-danger, #ef4444)' }} />
            )}
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <div className="text-truncate" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>{file.name}</div>
              <div className="small" style={{ color: 'var(--pp-text-secondary)' }}>{(file.size / 1024).toFixed(0)} KB</div>
            </div>
            <Button text rounded icon="pi pi-times" onClick={reset} aria-label={t('video_wall.library.cancel')} />
          </div>
          {error && <Message severity="error" text={error} className="w-100 mb-3" />}
          <div className="d-flex justify-content-end gap-2">
            <Button text label={t('video_wall.menu_ocr.change')} onClick={() => fileRef.current?.click()} />
            <Button icon="pi pi-sparkles" label={t('video_wall.menu_ocr.read_menu')} onClick={doImport} />
          </div>
        </div>
      )}
    </Dialog>
  );
}
